import { useListApps } from "@package/api-client/hooks/appsController";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { AppWindow, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateApp } from "@/api/apps";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/")({
	component: OrgIndex,
});

function OrgIndex() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { data: apps, isLoading } = useListApps(orgId);
	const createApp = useCreateApp(orgId);

	const [showCreateForm, setShowCreateForm] = useState(false);
	const [newAppName, setNewAppName] = useState("");

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<span className="loading loading-spinner loading-lg" />
			</div>
		);
	}

	if (apps && apps.length > 0 && apps[0]) {
		return <Navigate to="/app/org/$orgId/app/$appId" params={{ orgId, appId: apps[0].id }} />;
	}

	const handleCreateApp = (e: React.FormEvent) => {
		e.preventDefault();
		if (!newAppName.trim()) return;
		createApp.mutate(
			{ orgId, data: { name: newAppName.trim() } },
			{
				onSuccess: () => {
					setNewAppName("");
					setShowCreateForm(false);
				},
			},
		);
	};

	return (
		<div className="h-full flex items-center justify-center">
			<div className="border-2 border-dashed border-base-300 rounded-xl p-12 text-center max-w-md">
				<AppWindow className="w-12 h-12 mx-auto text-base-content/30 mb-4" />
				<h3 className="font-semibold text-lg text-base-content/70">{t("apps.noApps")}</h3>
				<p className="text-sm text-base-content/50 mt-1">{t("apps.createFirst")}</p>
				<button
					type="button"
					className="btn btn-primary btn-sm mt-4"
					onClick={() => setShowCreateForm(true)}
				>
					<Plus className="w-4 h-4" />
					{t("apps.createNew")}
				</button>

				{showCreateForm && (
					<dialog className="modal modal-open">
						<div className="modal-box">
							<h3 className="font-bold text-lg">{t("apps.createNew")}</h3>
							<form onSubmit={handleCreateApp} className="mt-4">
								<div className="form-control">
									<label className="label" htmlFor="first-app-name-input">
										<span className="label-text">{t("apps.name")}</span>
									</label>
									<input
										id="first-app-name-input"
										type="text"
										className="input input-bordered w-full"
										placeholder={t("apps.name")}
										value={newAppName}
										onChange={(e) => setNewAppName(e.target.value)}
										maxLength={200}
										autoFocus
									/>
								</div>
								<div className="modal-action">
									<button
										type="button"
										className="btn"
										onClick={() => {
											setShowCreateForm(false);
											setNewAppName("");
										}}
									>
										{t("common.cancel")}
									</button>
									<button
										type="submit"
										className="btn btn-primary"
										disabled={!newAppName.trim() || createApp.isPending}
									>
										{createApp.isPending ? t("common.creating") : t("common.create")}
									</button>
								</div>
							</form>
						</div>
						<form method="dialog" className="modal-backdrop">
							<button
								type="button"
								onClick={() => {
									setShowCreateForm(false);
									setNewAppName("");
								}}
							>
								close
							</button>
						</form>
					</dialog>
				)}
			</div>
		</div>
	);
}
